"use client";

// Shared error-boundary UI for the app shell.
//
// Two error.tsx files share this:
//   - [locale]/error.tsx — catches errors thrown in (app)/layout.tsx
//     itself (most importantly `getCurrentUser()` re-throwing after
//     auth.ts exhausts its retry budget). A boundary at the *same*
//     segment as (app)/layout.tsx wouldn't catch this — Next.js
//     nests error.tsx inside its sibling layout.
//   - (app)/error.tsx — catches errors thrown in pages under (app)
//     so a page crash doesn't wipe the sidebar/topbar shell.
//
// Strategy: detect the "fetch failed" / network shape and poll
// /api/healthz until the API answers, then call Next's `reset()` to
// re-render the failed segment. Tab self-heals once the new uvicorn
// binds, no manual refresh required. Non-network errors fall through
// to a static "Try again" UI — those probably warrant the operator's
// attention, not silent retry.

import { useEffect, useState } from "react";

const HEALTH_URL = "/api/healthz";
const POLL_DELAYS_MS = [1000, 1500, 2500, 4000, 6000, 8000];

function _looksLikeNetwork(err: Error): boolean {
  const msg = String(err.message ?? "");
  // Raw undici / fetch failure shapes when origin is unreachable.
  return (
    msg === "fetch failed" ||
    msg.includes("ECONNREFUSED") ||
    msg.includes("ETIMEDOUT") ||
    msg.includes("ENOTFOUND") ||
    // Wrapped form used in our retry-then-throw branch in auth.ts.
    /TypeError: fetch failed/.test(msg)
  );
}

export function NetworkErrorRecovery({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const isNetwork = _looksLikeNetwork(error);
  const [attempt, setAttempt] = useState(0);
  const [recovered, setRecovered] = useState(false);

  useEffect(() => {
    if (!isNetwork || recovered) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let step = 0;

    async function probe() {
      if (cancelled) return;
      try {
        const res = await fetch(HEALTH_URL, {
          method: "GET",
          credentials: "include",
          cache: "no-store",
        });
        if (!cancelled && res.ok) {
          setRecovered(true);
          // Brief beat so the operator sees the "Reconnected" state
          // before the page snaps back.
          timer = setTimeout(() => {
            if (!cancelled) reset();
          }, 300);
          return;
        }
      } catch {
        // Still down — keep polling.
      }
      step = Math.min(step + 1, POLL_DELAYS_MS.length - 1);
      setAttempt((n) => n + 1);
      timer = setTimeout(probe, POLL_DELAYS_MS[step]);
    }
    void probe();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isNetwork, recovered, reset]);

  if (!isNetwork) {
    return (
      <div className="mx-auto mt-24 max-w-lg p-6 text-center">
        <h1 className="m-0 font-display text-[22px] font-medium">
          Something went wrong
        </h1>
        <p className="mt-2 text-[13px] text-[var(--muted-fg)]">
          {error.message || "An unexpected error occurred."}
        </p>
        {error.digest ? (
          <p className="mt-2 font-mono text-[11px] text-[var(--subtle-fg)]">
            digest: {error.digest}
          </p>
        ) : null}
        <button
          type="button"
          onClick={reset}
          className="mt-6 rounded-md border border-[var(--hairline)] px-4 py-2 text-[12px] hover:border-[var(--accent)]"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-24 max-w-lg p-6 text-center">
      <h1 className="m-0 font-display text-[22px] font-medium">
        {recovered ? "Reconnected" : "Reconnecting…"}
      </h1>
      <p className="mt-2 text-[13px] text-[var(--muted-fg)]">
        {recovered
          ? "API is back. Restoring page…"
          : "API briefly unreachable — polling for recovery."}
      </p>
      {!recovered ? (
        <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[var(--subtle-fg)]">
          attempt {attempt + 1}
        </p>
      ) : null}
      <button
        type="button"
        onClick={reset}
        className="mt-6 rounded-md border border-[var(--hairline)] px-4 py-2 text-[12px] hover:border-[var(--accent)]"
      >
        Retry now
      </button>
    </div>
  );
}
